import React from 'react';
import { View } from 'react-native';
import { Text } from '@/components/ui/text';
import { useTranslation } from 'react-i18next';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import type { WorkoutSupersetRecord } from '~/powersync/generated/schema';
import { ChatSupersetGroup } from './ChatSupersetGroup';
import { PresentationCaption } from './PresentationCaption';

interface WorkoutSupersetCardProps {
  caption?: string | null;
  superset: WorkoutSupersetRecord;
}

interface WorkoutSupersetCardSyncedProps {
  payload: Record<string, unknown>;
}

interface WorkoutSupersetPayload {
  caption?: string | null;
  supersetId?: string;
}

/**
 * `present_superset` (design izkig) → a "Superset" card with its member
 * exercises as collapsible interactive rows.
 */
export const WorkoutSupersetCard: React.FC<WorkoutSupersetCardProps> & {
  Synced: React.FC<WorkoutSupersetCardSyncedProps>;
} = ({ caption, superset }) => (
  <View className="w-full py-2 gap-2">
    <PresentationCaption caption={caption} />
    <ChatSupersetGroup superset={superset} />
  </View>
);

const WorkoutSupersetCardSynced: React.FC<WorkoutSupersetCardSyncedProps> = ({
  payload,
}) => {
  const { t } = useTranslation('workout');
  const { caption, supersetId } = payload as WorkoutSupersetPayload;

  const { data: superset, isLoading } =
    useLivePowerSyncQuery<WorkoutSupersetRecord>(
      supersetId ? db.Workout.queries.findSupersetById(supersetId) : null,
      [supersetId],
      { single: true },
    );

  if (!supersetId) {
    return null;
  }

  if (isLoading) {
    return (
      <View className="w-full py-2 gap-2">
        <PresentationCaption caption={caption} />
        <View className="bg-surface rounded-2xl p-4">
          <Text className="text-text-muted text-sm">{t('sets.loading')}</Text>
        </View>
      </View>
    );
  }

  if (!superset) {
    return null;
  }

  return <WorkoutSupersetCard caption={caption} superset={superset} />;
};

WorkoutSupersetCard.Synced = WorkoutSupersetCardSynced;
